import { useId } from "react";

import { cn } from "@/lib/utils";

interface SparklineProps {
  /** Length 7, oldest → newest. Nulls = no data that day; do not interpolate. */
  values: (number | null)[];
  className?: string;
  /** Tone drives the stroke colour. Defaults to neutral muted. */
  tone?: "positive" | "negative" | "neutral";
}

/**
 * Tiny inline-SVG sparkline. We deliberately avoid Recharts here because each
 * KPI card embeds one and Recharts' per-instance overhead piles up quickly
 * once the page hits 8 KPIs across two platforms.
 *
 * Null handling: a null breaks the stroke into separate path segments rather
 * than getting interpolated. This honours the backend's "no data that day"
 * semantic — interpolating would invent a trend line that doesn't exist.
 */
export function Sparkline({ values, className, tone = "neutral" }: SparklineProps) {
  const gradientId = useId();
  const width = 80;
  const height = 28;
  const padding = 2;

  const numeric = values.filter((v): v is number => v != null);
  if (numeric.length < 2) {
    return (
      <div
        className={cn(
          "flex h-7 w-20 items-center justify-center text-[10px] text-muted-foreground/60",
          className,
        )}
      >
        no trend
      </div>
    );
  }

  const min = Math.min(...numeric);
  const max = Math.max(...numeric);
  const range = max - min || 1;

  const stepX = (width - padding * 2) / Math.max(values.length - 1, 1);
  const points = values.map((v, i) => {
    if (v == null) return null;
    const x = padding + i * stepX;
    const y = padding + (1 - (v - min) / range) * (height - padding * 2);
    return { x, y };
  });

  // Build broken-up path segments so consecutive points are joined but null
  // gaps stay broken.
  const segments: { x: number; y: number }[][] = [];
  let current: { x: number; y: number }[] = [];
  for (const p of points) {
    if (p == null) {
      if (current.length) segments.push(current);
      current = [];
    } else {
      current.push(p);
    }
  }
  if (current.length) segments.push(current);

  const stroke =
    tone === "positive"
      ? "var(--color-success, #16a34a)"
      : tone === "negative"
        ? "var(--color-destructive, #dc2626)"
        : "var(--color-muted-foreground, #6b7280)";

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className={cn("h-7 w-20 shrink-0", className)}
      aria-hidden="true"
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={stroke} stopOpacity="0.18" />
          <stop offset="100%" stopColor={stroke} stopOpacity="0" />
        </linearGradient>
      </defs>
      {segments.map((seg, idx) => {
        if (seg.length < 2) return null;
        const d = seg.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join(" ");
        const fillD = `${d} L ${seg[seg.length - 1].x.toFixed(2)} ${height} L ${seg[0].x.toFixed(2)} ${height} Z`;
        return (
          <g key={idx}>
            <path d={fillD} fill={`url(#${gradientId})`} />
            <path d={d} fill="none" stroke={stroke} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
          </g>
        );
      })}
    </svg>
  );
}
